/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { pickBy } from 'ramda';
import { useSelector } from 'react-redux';
import { IS_BIZ_USER, IS_BLOCKED, IS_PUBLIC_ALBUM, IS_PUBLIC_FOLDER } from 'reactApp/appHelpers/configHelpers';
import { isActivitiesFeatureEnabled, isQuotaCleanerMobLetterDownloadAvailable } from 'reactApp/appHelpers/featuresHelpers';
import { overquotaAbClearDownloadVarBHideDownload } from 'reactApp/appHelpers/featuresHelpers/features/overquotaAbClearDownload';
import { overquotaClearAbSelectAll } from 'reactApp/appHelpers/featuresHelpers/features/overquotaClearAbSelectAll';
import { seoEnable } from 'reactApp/appHelpers/featuresHelpers/features/seoEnable';
import { hasAuthorizationInfoWithVersion } from 'reactApp/appHelpers/featuresHelpers/features/superAppAuthorizationInfo';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useIsWindowHeightLess } from 'reactApp/hooks/useIsWindowHeightLess';
import { CUSTOM_ALBUMS_ID } from 'reactApp/modules/albums/albums.constants';
import { isAddToAlbumsEnabled, isAlbumFolderCustom, isCreatePublicAlbumsEnabled } from 'reactApp/modules/albums/albums.selector';
import { isAlbum } from 'reactApp/modules/albums/albums.types';
import { EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { getBizFilteredItems } from 'reactApp/modules/bizCategories/bizCategories.helpers';
import { getBizCurrentCategory } from 'reactApp/modules/bizCategories/bizCategories.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { hasFolderFileWithFace } from 'reactApp/modules/faces/faces.selectors';
import { isFolder } from 'reactApp/modules/file/utils';
import { isDefaultRootContent } from 'reactApp/modules/home/home.selectors';
import {
    getPublicRootWeblink,
    isDownloadActionAvailable,
    isOwnPublic,
    isPublicUploadEnabled,
} from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isDomainFolder, isMountedFolder } from 'reactApp/modules/storage/folder.helpers';
import { INLINE_INTEGRATION_STORAGE_FULL, STORAGE_CONFIG } from 'reactApp/modules/storage/storage.config';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import {
    getFavoritesStorageItems,
    getStorageCurrentFolder,
    getStorageItemById,
    getStorageSelectedItems,
    hasParentMountedOrSharedFolder,
    isMountedOrSharedFolder,
    isReadOnlyMountedOrShared,
    isRootCurrentFolder,
} from 'reactApp/modules/storage/storage.selectors';
import { type CloudItem, EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { getCurrentGroup } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import type { RootState } from 'reactApp/store';
import { UserQuotaGroupType } from 'reactApp/types/QuotaCleaner';
import { isItemsEmailUrlOk } from 'reactApp/ui/ContextMenu/createContextMenuList';
import { EQueryParams } from 'server/helpers/getRequestParams';

interface UseActionsConfig {
    showDisabled?: boolean;
    useOnlyCurrentFolder?: boolean;
    id?: string;
    storage?: EStorageType;
    skipUpload?: boolean;
    skipRename?: boolean;
    skipDelete?: boolean;
    isMobile?: boolean;
}

export const useActionsConfig = ({
    showDisabled = false,
    useOnlyCurrentFolder = false,
    skipUpload = false,
    skipRename = false,
    skipDelete = false,
    storage,
    id,
    isMobile = false,
}: UseActionsConfig = {}): Partial<Record<EActions, boolean>> => {
    const actions: Partial<Record<EActions, boolean>> = {};

    const currentStorage = useSelector(getCurrentStorage);

    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isAnonymous = useSelector(UserSelectors.isAnonymous);

    const {
        isHome,
        isPublic,
        isFavorites,
        isAttaches,
        isStock,
        isSharedIncoming,
        isSharedLinks,
        isSharedAutodelete,
        isAlbums,
        isPdfEdit,
        isQuotaCleaner,
        isSearch,
        isInlineIntegration,
        isTrashBin,
    } = getStorage(storage ?? currentStorage);
    const storageSelectedItems = useSelector((state: RootState) =>
        getStorageSelectedItems(state, storage ?? currentStorage)
    ) as CloudItem[];
    const itemById = useSelector((state: RootState) => {
        return getStorageItemById(state, storage ?? currentStorage, id);
    });
    const items = useOnlyCurrentFolder ? [] : itemById ? [itemById] : storageSelectedItems;
    const isSelectedMounted = items.some(isMountedFolder);
    const selectedCount = items.length;
    const oneItem = selectedCount === 1;
    const isOneItemFolder = oneItem && items[0].isFolder;
    const itemsWithWeblink = items.some((item) => 'weblink' in item && item?.weblink);
    const itemsCloudAttaches = isAttaches && items.some((item) => 'attachType' in item && item?.attachType === EAttachTypes.cloud);
    const currentFolder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage ?? currentStorage));
    const isOwn = useSelector(isOwnPublic);
    const isMountedOrSharedFolderStatus = useSelector((state: RootState) => isMountedOrSharedFolder(state, currentFolder));
    const isCurrentSharedOrMounted = isMountedOrSharedFolderStatus.is;
    const isReadOnly = useSelector((state: RootState) => isReadOnlyMountedOrShared(state, currentFolder));
    const isUploadEnabled = useSelector(isPublicUploadEnabled);
    const isDownloadEnabled = useSelector(isDownloadActionAvailable);
    const itemsOrCurrentFolder = items.length ? items : [currentFolder];
    const isPublicAlbumEnabled = useSelector((state: RootState) => isCreatePublicAlbumsEnabled(state, itemsOrCurrentFolder));
    const isItemAlbum = (oneItem && items.every(isAlbum)) || (selectedCount === 0 && isAlbum(currentFolder));
    const { weblink = '' } = useSelector(getPublicRootWeblink) || {};
    const hasFaces = useSelector((state: RootState) => hasFolderFileWithFace(state, weblink));
    const isAlbumCustomFolder = useSelector(isAlbumFolderCustom);
    const isAddToAlbumsAvailable = useSelector((state: RootState) => isAddToAlbumsEnabled(state, itemsOrCurrentFolder));
    const isDefaultContent = useSelector(isDefaultRootContent);
    const isNotRootFolder = Boolean(currentFolder) && currentFolder?.id !== ROOT_FOLDER_ID;
    const itemsWithDomain = items.length ? items.some(isDomainFolder) : currentFolder && isDomainFolder(currentFolder);
    const hasParentMountedOrShared = useSelector((state: RootState) => hasParentMountedOrSharedFolder(state, items));
    const activeDownloadInFavouritesForBizUser = IS_BIZ_USER && (storage ?? currentStorage) === EStorageType.favorites;
    const favItems = useSelector(getFavoritesStorageItems);
    const bizCurrentCategory = useSelector(getBizCurrentCategory);
    const favBizFilteredItems = getBizFilteredItems(favItems, bizCurrentCategory);
    const currentCleanerGroup = useSelector(getCurrentGroup);
    const isEmptyFolder = activeDownloadInFavouritesForBizUser ? !favBizFilteredItems.length : !currentFolder?.childs?.length;
    const isWindowSmall = useIsWindowHeightLess();
    const isWebView = useSelector(EnvironmentSelectors.isWebview);
    const fromDeeplink = getQueryParams()[EQueryParams.fromDeeplink];
    const isOverquota = useSelector(UserQuotaSelectors.isOverquotaBySpaceAndApi);

    const isRootFolder = useSelector((state: RootState) => isRootCurrentFolder(state, currentStorage));
    const isAlbumsRootFolder = isAlbums && isRootFolder;

    const isPublicSuperAppWebView = isWebView && fromDeeplink && isPublic;
    const showSuperAppControlButtons: boolean = isPublicSuperAppWebView ? hasAuthorizationInfoWithVersion(isAnonymous) : true;

    const settings =
        isInlineIntegration && !isWindowSmall ? STORAGE_CONFIG[INLINE_INTEGRATION_STORAGE_FULL] : STORAGE_CONFIG[storage || currentStorage];

    if (settings?.facesFilter) {
        actions[EActions.facesFilter] = hasFaces && Boolean(IS_PUBLIC_FOLDER);
    }

    if (settings?.back) {
        actions[EActions.back] = true;
    }

    if (settings?.selectAll) {
        let selectingAvailable = Boolean(currentFolder);

        if (isSharedAutodelete) {
            selectingAvailable = !isEmptyFolder;
        }

        if (isAlbums) {
            selectingAvailable = selectingAvailable && currentFolder?.id !== CUSTOM_ALBUMS_ID;
        }

        if (isQuotaCleaner && isOverquota && overquotaClearAbSelectAll) {
            selectingAvailable = false;
        }

        actions[EActions.selectAll] = selectingAvailable;
    }

    if (settings?.restoreAll && !isBizUser) {
        actions[EActions.restoreAll] = !selectedCount && !isEmptyFolder;
    }

    if (settings?.restore) {
        actions[EActions.restore] = Boolean(selectedCount);
    }

    if (settings?.clear) {
        actions[EActions.clear] = !isEmptyFolder;
    }

    if (settings?.createFolder) {
        actions[EActions.createFolder] = !isReadOnly;
    }

    if (settings?.publish && !IS_BLOCKED) {
        const canPublishAlbum = !isAlbums && isPublicAlbumEnabled;
        let publishAvailable = (oneItem && !canPublishAlbum) || (isPdfEdit && !!itemById);

        if (isHome) {
            publishAvailable = publishAvailable || (!selectedCount && isNotRootFolder);
        }

        if (isAlbums) {
            publishAvailable = isItemAlbum && !isAlbumCustomFolder && !isAlbumsRootFolder && isPublicAlbumEnabled;
        }

        if (isPublicAlbumEnabled) {
            actions[EActions.publishAlbum] = true;
        }

        if (itemsCloudAttaches) {
            publishAvailable = false;
        }

        // CLOUD-4594: share available only for non-family folders
        actions[EActions.publish] =
            publishAvailable && ((currentFolder as any)?.kind !== 'family' || (oneItem && items[0]?.kind !== 'family'));

        if ((IS_BIZ_USER && selectedCount >= 1 && itemsWithWeblink) || (!publishAvailable && itemsWithWeblink)) {
            actions[EActions.unpublish] = true;
        }
    }

    if (isAddToAlbumsAvailable && !isAlbumCustomFolder && !isAlbumsRootFolder) {
        actions[EActions.addToCustomAlbum] = true;
    }

    if (settings?.share) {
        let shareAvailable = isOneItemFolder;

        if (isHome) {
            shareAvailable =
                !itemsWithDomain &&
                (isCurrentSharedOrMounted
                    ? // нельзя "Настроить доступ" у подпапок примонтированной папки
                      !selectedCount && isMountedOrSharedFolderStatus.folderId === currentFolder?.id
                    : !selectedCount || isOneItemFolder);
        } else if (isSharedLinks) {
            shareAvailable = !selectedCount || isOneItemFolder;
        } else if (isSharedIncoming) {
            shareAvailable = isOneItemFolder && isSelectedMounted;
        } else if (isSearch) {
            shareAvailable = !hasParentMountedOrShared && isOneItemFolder;
        }

        // CLOUD-4594: share available only for non-family folders
        actions[EActions.share] = shareAvailable && ((currentFolder as any)?.kind !== 'family' || (oneItem && items[0]?.kind !== 'family'));
    }

    if (settings?.shareLink) {
        // CLOUD-4594: share available only for non-family folders
        actions[EActions.shareLink] =
            (oneItem || !selectedCount) && ((currentFolder as any)?.kind !== 'family' || (oneItem && items[0]?.kind !== 'family'));
    }

    if (settings?.clone && !isOwn && !itemsCloudAttaches && !IS_BLOCKED && showSuperAppControlButtons) {
        actions[EActions.clone] = isAttaches ? selectedCount > 0 : true;
    }

    if (settings?.download || activeDownloadInFavouritesForBizUser) {
        let downloadAvailable = !!selectedCount;

        if (isAttaches) {
            downloadAvailable = Boolean(selectedCount);
        } else if (isHome) {
            downloadAvailable = !isEmptyFolder && !isDefaultContent;
        } else if (isFavorites) {
            downloadAvailable = !isEmptyFolder;
        } else if (isPublic) {
            downloadAvailable = isDownloadEnabled && (!isEmptyFolder || storageSelectedItems?.length > 0);
        } else if (isStock) {
            downloadAvailable = !isEmptyFolder || storageSelectedItems?.length > 0;
        } else if (isSharedIncoming) {
            downloadAvailable = isSelectedMounted;
        } else if (isAlbums) {
            downloadAvailable = !isAlbumCustomFolder && !isEmptyFolder && !isAlbumsRootFolder;
        } else if (isQuotaCleaner) {
            if (
                (selectedCount > 1 || !isQuotaCleanerMobLetterDownloadAvailable) &&
                currentCleanerGroup?.groupType === UserQuotaGroupType.Letter
            ) {
                downloadAvailable = false;
            } else {
                downloadAvailable = true;
            }
            if (downloadAvailable && isOverquota && overquotaAbClearDownloadVarBHideDownload) {
                downloadAvailable = false;
                showDisabled = false;
            }
        } else if (isInlineIntegration) {
            // Запрещаем скачивать папки, т.к. пока не реализовано скачивание архивов
            // будет сделано после просмотрщика архивов
            downloadAvailable = !items.some(isFolder);
        }

        actions[EActions.download] = downloadAvailable;
    }

    if (settings?.sendViaMail) {
        actions[EActions.sendViaMail] = Boolean(selectedCount) && isItemsEmailUrlOk(items);
    }

    if (settings?.add && showSuperAppControlButtons) {
        actions[EActions.upload] = ((isPublic && (isOwn || isUploadEnabled)) || !isPublic) && !skipUpload && !isTrashBin;
    }

    if (settings?.favorites && !isMountedOrSharedFolderStatus.isMounted) {
        const allInFavorites = (items as CloudItem[]).every((item) => 'isInFavorites' in item && item.isInFavorites);
        const mountedItems = (items as CloudItem[]).filter((item) => 'isMounted' in item && item.isMounted && !item.isFolder);

        actions[EActions.addToFavorites] =
            isAlbumCustomFolder || isAlbumsRootFolder ? false : !allInFavorites && !mountedItems.length && !hasParentMountedOrShared;
        actions[EActions.removeFromFavorites] =
            isAlbumCustomFolder || isAlbumsRootFolder ? false : Boolean(selectedCount) && allInFavorites;
    }

    if (settings?.mount) {
        actions[EActions.mount] = !isSelectedMounted && oneItem;
    }

    if (settings?.copy && !itemsWithDomain) {
        let copyAvailable = Boolean(selectedCount);

        if (isHome) {
            copyAvailable = copyAvailable || isNotRootFolder;
        }

        actions[EActions.copy] = copyAvailable;
    }

    if (settings?.move) {
        let moveAvailable = Boolean(selectedCount) && !isReadOnly && !itemsWithDomain && !(isSelectedMounted && IS_BIZ_USER);

        if (isHome) {
            moveAvailable =
                moveAvailable ||
                (!selectedCount &&
                    ((isNotRootFolder && !itemsWithDomain && !isReadOnly) || (isReadOnly && isCurrentSharedOrMounted && !itemsWithDomain)));
        }

        if (IS_BIZ_USER && isMountedOrSharedFolderStatus.isMounted && !selectedCount) {
            moveAvailable = false;
        }

        actions[EActions.move] = moveAvailable;
    }

    if (settings?.rename) {
        let renameAvailable = oneItem && !isReadOnly && !itemsWithDomain;

        if (isHome) {
            renameAvailable =
                renameAvailable ||
                (!selectedCount &&
                    ((isNotRootFolder && !itemsWithDomain && !isReadOnly) || (isReadOnly && isCurrentSharedOrMounted && !itemsWithDomain)));
        }

        if (isAlbums) {
            renameAvailable = isItemAlbum && !isAlbumCustomFolder && !isAlbumsRootFolder;
        }

        actions[EActions.rename] = renameAvailable && !skipRename;
    }

    if (settings?.history) {
        actions[EActions.history] = oneItem && ((!isOneItemFolder && !isReadOnly) || (isActivitiesFeatureEnabled && isOneItemFolder));
    }

    if (settings?.remove && !skipDelete) {
        let removeAvailable = Boolean(selectedCount) && !itemsWithDomain;

        if (isHome) {
            removeAvailable =
                !itemsWithDomain &&
                ((selectedCount && !isReadOnly) ||
                    (!selectedCount && !isReadOnly && isNotRootFolder) ||
                    (!selectedCount && isReadOnly && isCurrentSharedOrMounted));
        } else if (isPublic) {
            if (isMobile) {
                removeAvailable = IS_PUBLIC_ALBUM
                    ? removeAvailable && isOwn && !isItemAlbum
                    : (!itemById && Boolean(selectedCount) && isOwn) ||
                      (Boolean(itemById) && !itemById?.isFolder && (isOwn || isUploadEnabled));
            } else {
                removeAvailable = IS_PUBLIC_ALBUM ? removeAvailable && isOwn : removeAvailable && isUploadEnabled && !isAnonymous;
            }
        } else if (isSharedIncoming) {
            removeAvailable = removeAvailable && isOneItemFolder;
        } else if (isAlbums) {
            removeAvailable = !isAlbumCustomFolder && !isAlbumsRootFolder;
        }

        actions[EActions.remove] = removeAvailable;
    }

    if (settings?.complain && isPublic && seoEnable && !IS_BIZ_USER) {
        actions[EActions.complain] = true;
    }

    return showDisabled ? actions : pickBy(Boolean, actions);
};
